import { makeAutoObservable } from "mobx";
import type { AuthenticationStore, FintechRegisterStore } from "../../../stores";
import { LoaderShelf } from "../../../shelves";
import type { apiTypes } from "../../../services";
import { api } from "../../../services";
import type { AxiosError } from "axios";
import { invertDate, switchSlashesToHyphens } from "../../../utils/formatters";
import { VIVO } from "../../../constants";

export enum FintechRegistrationReqEnum {
	LOADING = "LOADING",
	SUCCESS = "SUCCESS",
	ERROR = "ERROR",
}

export class Store {
	public loader = new LoaderShelf();
	private authToken = "";

	constructor(public authentication: AuthenticationStore, public fintechRegisterStore: FintechRegisterStore) {
		this.authToken = authentication.getToken() || "";

		makeAutoObservable(this);
	}

	public get personalInfo(): apiTypes.PersonalInfo {
		return {
			cpfNumber: this.authentication.currentUser?.documentNumber || "",
			birthDate: invertDate(switchSlashesToHyphens(this.fintechRegisterStore.birthDate.field.value)),
			fullName: this.fintechRegisterStore.name.field.value.trim() || "",
		};
	}

	// Retorna os dados do cliente referente ao telefone
	public get personalContactPhoneInfo(): apiTypes.PersonalContactPhoneInfo {
		return {
			provider: VIVO,
			countryCode: "55",
			isMain: true,
			areaCode: this.authentication.currentUser?.phoneNumber.substring(0, 2) || "",
			number: this.authentication.currentUser?.phoneNumber.substring(2) || "",
		};
	}

	// Salva os dados do cliente personalContactPhoneInfo()
	public saveBasicPersonalContactPhoneInfo = async (): Promise<FintechRegistrationReqEnum> => {
		if (this.loader.isLoading) {
			return FintechRegistrationReqEnum.LOADING;
		}

		this.loader.start();

		try {
			// verifica se número usado no login é o mesmo da fintechRegisterStore (Dado salvo na fullRegistration)
			if (!this.fintechRegisterStore.personalPhone.value?.number) {
				await api.postCustomersPersonalContactsPhones(this.personalContactPhoneInfo, this.authToken);
				// salva o número de telefone do usuário na fintechRegisterStore
				this.fintechRegisterStore.personalPhone.set(this.personalContactPhoneInfo);
			} else if (!this.fintechRegisterStore.personalPhone.value.isMain) {
				await api.patchCustomersPersonalContactsPhones(
					this.fintechRegisterStore.personalPhone.value.id || "",
					this.personalContactPhoneInfo,
					this.authToken,
				);
			}

			return FintechRegistrationReqEnum.SUCCESS;
		} catch (e) {
			window.console.error(e);
			return FintechRegistrationReqEnum.ERROR;
		} finally {
			this.loader.end();
		}
	};

	// Salva os dados do cliente personalInfo()
	public saveBasicPersonalInfo = async (): Promise<FintechRegistrationReqEnum> => {
		if (this.loader.isLoading) {
			return FintechRegistrationReqEnum.LOADING;
		}

		this.loader.start();
		try {
			await api.postCustomersPersonal(this.personalInfo, this.authToken);

			return FintechRegistrationReqEnum.SUCCESS;
		} catch (e) {
			const error = e as AxiosError;
			// Em caso de conflito, tenta atualizar os dados
			if (error.response?.status === 409) {
				return await this.patchCustomersPersonal();
			}

			return FintechRegistrationReqEnum.ERROR;
		} finally {
			this.loader.end();
		}
	};

	// Patch nos dados do cliente personalInfo()
	private patchCustomersPersonal = async (): Promise<FintechRegistrationReqEnum> => {
		try {
			await api.patchCustomersPersonal(this.personalInfo, this.authToken);

			return FintechRegistrationReqEnum.SUCCESS;
		} catch (e) {
			window.console.error(e);
			return FintechRegistrationReqEnum.ERROR;
		}
	};
}
